
import React from 'react';
import NavigationBar from '@/components/landing/NavigationBar';
import Footer from '@/components/landing/Footer';
import { Helmet } from 'react-helmet';

const CompetitiveAnalysis = () => {
  return (
    <>
      <Helmet>
        <title>Vendor & Supplier Competition Analysis | AnalystX</title>
        <meta name="description" content="Gain strategic advantage with our AI-powered vendor competition and supplier analysis tools. Compare competitors, identify strengths and weaknesses." />
        <meta name="keywords" content="vendor competition, supplier competition, competitive analysis, competitor comparison, market intelligence" />
      </Helmet>
      
      <div className="min-h-screen bg-gray-900 text-white">
        <NavigationBar />
        
        <main className="container mx-auto px-4 py-16">
          <h1 className="text-4xl font-bold mb-6">Vendor & Supplier Competition Analysis</h1>
          
          <div className="bg-gray-800/50 rounded-xl p-6 mb-12">
            <h2 className="text-2xl font-semibold mb-4">Strategic Competitive Intelligence</h2>
            <p className="text-gray-300 mb-4">
              Understanding your competitive landscape is essential for strategic decision-making. AnalystX provides comprehensive vendor competition and supplier analysis to help you identify strengths, weaknesses, opportunities, and threats in your market.
            </p>
            <p className="text-gray-300 mb-4">
              Our AI-powered platform analyzes thousands of data points across competitors, offering detailed comparisons of products, services, pricing strategies, market positioning, and customer satisfaction metrics.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
            <div className="bg-gray-800/30 rounded-xl p-6">
              <h3 className="text-xl font-semibold mb-3">Vendor Competition Analysis</h3>
              <ul className="list-disc list-inside space-y-2 text-gray-300">
                <li>Comprehensive competitor profiles</li>
                <li>Product and service offering comparisons</li>
                <li>Pricing strategy analysis</li>
                <li>Marketing approach evaluation</li>
                <li>Customer experience assessment</li>
                <li>SWOT analysis for each competitor</li>
              </ul>
            </div>
            
            <div className="bg-gray-800/30 rounded-xl p-6">
              <h3 className="text-xl font-semibold mb-3">Supplier Competition Insights</h3>
              <ul className="list-disc list-inside space-y-2 text-gray-300">
                <li>Supply chain efficiency comparisons</li>
                <li>Reliability and quality metrics</li>
                <li>Pricing and contract term analysis</li>
                <li>Geographic coverage assessment</li>
                <li>Innovation and technology evaluation</li>
                <li>Sustainability and ESG performance</li>
              </ul>
            </div>
          </div>
          
          <div className="bg-blue-900/30 rounded-xl p-6 border border-blue-800/50 mb-12">
            <h2 className="text-2xl font-semibold mb-4">Competitive Intelligence Applications</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <h3 className="text-lg font-medium mb-2">Strategic Planning</h3>
                <p className="text-gray-300">Inform your strategic decisions with detailed competitive landscape analysis.</p>
              </div>
              <div>
                <h3 className="text-lg font-medium mb-2">Product Development</h3>
                <p className="text-gray-300">Identify gaps in the market and opportunities for differentiation.</p>
              </div>
              <div>
                <h3 className="text-lg font-medium mb-2">Sales Enablement</h3>
                <p className="text-gray-300">Equip your sales team with competitive intelligence to win more deals.</p>
              </div>
            </div>
          </div>
          
          <div className="bg-gray-800/50 rounded-xl p-6">
            <h2 className="text-2xl font-semibold mb-4">Continuous Competitive Monitoring</h2>
            <p className="text-gray-300 mb-4">
              Markets and competitors evolve constantly. AnalystX provides ongoing monitoring of your competitive landscape, alerting you to significant changes, new entrants, and emerging threats or opportunities.
            </p>
            <p className="text-gray-300">
              Stay ahead of market shifts with real-time vendor and supplier competition intelligence that keeps you informed of developments as they happen.
            </p>
          </div>
        </main>
        
        <Footer />
      </div>
    </>
  );
};

export default CompetitiveAnalysis;
